/**
 * Shared utility to map API product data to ProductCard format
 * Used by both ProductList and ProductSearch components
 */

import {
  formatProductUsdPrice,
  getProductPerUnitLine,
} from "@b2b/ui-components";

export interface ProductCardData {
  id: string;
  slug?: string;
  title: string;
  price: string;
  /** Shown below price on cards, e.g. "Per pieces" (matches product detail). */
  unitPrice?: string;
  moq: string;
  score: number;
  supplier: {
    name: string;
    rating: number;
    verified: boolean;
  };
  image: string;
}

function formatCardListPrice(product: any): string {
  if (product.priceMin != null && product.priceMax != null) {
    return `${formatProductUsdPrice(Number(product.priceMin))} - ${formatProductUsdPrice(Number(product.priceMax))}`;
  }
  return formatProductUsdPrice(Number(product.price || 0));
}

/**
 * Maps API product response to ProductCard format
 * @param product - Product object from API
 * @returns ProductCardData formatted for ProductCard component
 */
export function mapProductToCardFormat(product: any): ProductCardData {
  const unitLine = getProductPerUnitLine(product);
  return {
    id: product.id,
    slug: product.slug || undefined,
    title: product.name,
    price: formatCardListPrice(product),
    unitPrice: unitLine || undefined,
    moq: product.moq
      ? `${product.moq} ${product.moqUnit || "pieces"}`
      : "1 piece",
    score: 4.5, // Default score, can be enhanced later with actual ratings
    supplier: {
      name: product.vendor?.name || product.company?.name || "Supplier",
      rating: 4, // Default rating, can be enhanced later with actual ratings
      verified: true, // Default verified status
    },
    image:
      product.thumbnailImage ||
      (product.images && Array.isArray(product.images) && product.images.length > 0
        ? typeof product.images[0] === 'string'
          ? product.images[0]
          : product.images[0].url || product.images[0]
        : ""),
  };
}

/**
 * Maps array of API products to ProductCard format
 * @param products - Array of product objects from API
 * @returns Array of ProductCardData
 */
export function mapProductsToCardFormat(products: any[]): ProductCardData[] {
  return products.map(mapProductToCardFormat);
}

export {
  buildProductTradeSpecRows,
  formatProductUsdPrice,
  getProductUnitLabel,
  getProductPerUnitLine,
} from "@b2b/ui-components";

